import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';
const STORAGE_KEY = 'potential_patterns_task_id';
const META_STORAGE_KEY = 'potential_patterns_task_meta';

export interface ScanProgress {
  current: number;
  total: number;
  symbol: string;
}

export interface ScanResult {
  [key: string]: any;
}

// What a scan is actually FOR — lets a background scan started on one timeframe/mode be
// tracked correctly (and its result written to the right cache slot) no matter which
// timeframe/mode the user is currently looking at. See ScanProgressWidget for the
// always-mounted UI this enables.
export interface ScanMeta {
  interval: string;
  rankMode: 'default' | 'custom' | 'watchlist';
  startRank?: number;
  endRank?: number;
}

interface ScanContextValue {
  taskId: string | null;
  isScanning: boolean;
  progress: ScanProgress | null;
  results: ScanResult[];
  error: string | null;
  meta: ScanMeta | null;
  startScan: (taskId: string, meta: ScanMeta) => void;
  clearScan: () => void;
  setResults: (results: ScanResult[]) => void;
}

const ScanContext = createContext<ScanContextValue | null>(null);

function readStoredMeta(): ScanMeta | null {
  try {
    const raw = localStorage.getItem(META_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ScanMeta) : null;
  } catch {
    return null;
  }
}

export const ScanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [taskId, setTaskId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [results, setResults] = useState<ScanResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<ScanMeta | null>(() => readStoredMeta());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track the active task ID in a ref so in-flight poll callbacks from a previous
  // scan can detect they're stale and discard their results instead of overwriting
  // the new scan's state.
  const activeTaskIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback((id: string) => {
    stopPolling();
    activeTaskIdRef.current = id;
    setIsScanning(true);

    intervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/potential-patterns/task/${id}`, {
          credentials: 'include'
        });

        // Discard if a newer scan has already started
        if (activeTaskIdRef.current !== id) return;

        if (res.status === 404) {
          stopPolling();
          setIsScanning(false);
          setProgress(null);
          setTaskId(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(META_STORAGE_KEY);
          return;
        }

        const data = await res.json();
        if (activeTaskIdRef.current !== id) return; // re-check after async gap

        if (!data.success || !data.task) {
          stopPolling();
          setIsScanning(false);
          setProgress(null);
          setTaskId(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(META_STORAGE_KEY);
          return;
        }

        const task = data.task;

        if (task.progress) {
          setProgress({
            current: task.progress.current,
            total: task.progress.total,
            symbol: task.progress.currentSymbol || ''
          });
        }

        if (task.status === 'completed') {
          setResults(task.result || []);
          setProgress(null);
          setIsScanning(false);
          setTaskId(null);
          // meta intentionally left in place — consumers route these final results using it
          // in the same pass; localStorage cleanup only, not the in-memory state.
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(META_STORAGE_KEY);
          stopPolling();
        } else if (task.status === 'failed') {
          setError(task.error || 'Scan failed');
          setProgress(null);
          setIsScanning(false);
          setTaskId(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(META_STORAGE_KEY);
          stopPolling();
        } else if (task.status === 'running' && task.partialResults && task.partialResults.length > 0) {
          // Show partial results as coins complete during the scan
          setResults(task.partialResults);
        }
      } catch {
        // Network blip — keep polling
      }
    }, 5000);
  }, [stopPolling]);

  // Resume polling if taskId is in localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setTaskId(stored);
      poll(stored);
    }
    return () => stopPolling();
  }, [poll, stopPolling]);

  const startScan = useCallback((id: string, scanMeta: ScanMeta) => {
    localStorage.setItem(STORAGE_KEY, id);
    localStorage.setItem(META_STORAGE_KEY, JSON.stringify(scanMeta));
    activeTaskIdRef.current = id; // mark before poll so any in-flight callbacks are immediately stale
    setTaskId(id);
    setMeta(scanMeta);
    setError(null);
    setResults([]);
    setProgress(null);
    poll(id);
  }, [poll]);

  const clearScan = useCallback(() => {
    activeTaskIdRef.current = null;
    stopPolling();
    setTaskId(null);
    setMeta(null);
    setIsScanning(false);
    setProgress(null);
    setError(null);
    setResults([]);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(META_STORAGE_KEY);
  }, [stopPolling]);

  // Reset on logout (explicit or token-expiry auto-logout) so the next account on
  // this device doesn't inherit this user's in-flight scan or results.
  useEffect(() => {
    window.addEventListener('lumina:logout', clearScan);
    return () => window.removeEventListener('lumina:logout', clearScan);
  }, [clearScan]);

  return (
    <ScanContext.Provider value={{ taskId, isScanning, progress, results, error, meta, startScan, clearScan, setResults }}>
      {children}
    </ScanContext.Provider>
  );
};

export const useScan = () => {
  const ctx = useContext(ScanContext);
  if (!ctx) throw new Error('useScan must be used within ScanProvider');
  return ctx;
};
